"use client"

import { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  // BarChart,
  // Bar,
  // PieChart,
  // Pie,
  // Cell,
  ResponsiveContainer,
} from "recharts"
import api from "../../services/api"
import Navbar from "../Layout/Navbar"
import CountryMap from "./CountryMap"
import MiniFooter from "../Layout/miniFooter"

const DEFAULT_RANGE = "30d"
const TOP_N = 5
// const COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6"]

const styles = {
  page: { background: "#fff", minHeight: "100vh", paddingTop: "2rem", paddingBottom: "3rem" },
  card: { background: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", overflow: "hidden" },
  cardHeader: { padding: "1.25rem 1.5rem", borderBottom: "1px solid #f1f5f9", background: "#fff" },
  cardTitle: { margin: 0, fontSize: "0.9375rem", fontWeight: 600, color: "#1e293b", display: "flex", alignItems: "center", gap: "0.5rem" },
  cardBody: { padding: "1.5rem" },
  statValue: { fontSize: "2rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 },
  statLabel: { fontSize: "0.8125rem", fontWeight: 500, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.025em", marginBottom: "0.5rem" },
  badge: { display: "inline-flex", alignItems: "center", gap: "0.25rem", padding: "0.25rem 0.625rem", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 600 },
  button: { display: "inline-flex", alignItems: "center", gap: "0.5rem", padding: "0.5rem 1rem", borderRadius: "8px", fontSize: "0.875rem", fontWeight: 500, cursor: "pointer", transition: "all 0.15s ease", border: "1px solid #e2e8f0", background: "#fff", color: "#475569" },
  buttonPrimary: { background: "#6366f1", color: "#fff", border: "none" },
  rangeButton: { padding: "0.375rem 0.875rem", borderRadius: "6px", fontSize: "0.8125rem", fontWeight: 500, cursor: "pointer", transition: "all 0.15s ease", border: "none" },
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "0.75rem 1rem", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
      <p style={{ margin: 0, fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>{label}</p>
      <p style={{ margin: 0, fontSize: "0.9375rem", fontWeight: 600, color: "#0f172a" }}>{payload[0].value.toLocaleString()} clicks</p>
    </div>
  )
}

const safe = (arr, key = "name") =>
  arr && arr.length ? arr.map(i => ({ ...i, name: i[key] ?? "Unknown", clicks: i.clicks ?? 0 })) : [{ name: "No Data", clicks: 0 }]

export default function PublicAnalytics() {
  const { code } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [dateRange ] = useState(DEFAULT_RANGE)
  const [lastUpdated, setLastUpdated] = useState(null)

  // --- Fetch Data ---
  useEffect(() => {
    if (!code) return

const fetchAnalytics = async () => {
  try {
    const res = await api.get(`/links/analytics/public/${code}?range=${dateRange}`)
    const d = res.data

    if (!d.success) {
      // 🔴 PLAN RESTRICTION HANDLING
      if (d.code === "PLAN_UPGRADE_REQUIRED" && d.redirect) {
        window.location.href = d.redirect
        return
      }

      setError(d.message || "Link not found.")
      return
    }

    setData(d.data)
    setLastUpdated(new Date())
  } catch (err) {
    // Axios error (403, 401, etc.)
    const apiError = err.response?.data

    if (apiError?.code === "PLAN_UPGRADE_REQUIRED" && apiError?.redirect) {
      window.location.href = apiError.redirect
      return
    }

    setError("Failed to fetch public analytics.")
  } finally {
    setLoading(false)
  }
}

    fetchAnalytics()
  }, [code, dateRange])

  
  if (loading) return <div style={{ textAlign: "center", marginTop: "5rem" }}>Loading...</div>
  if (error) return <div style={{ textAlign: "center", marginTop: "5rem", color: "red" }}>{error}</div>
  if (!data) return null


    const normalizeReferrers = (arr) => {
    if (!arr || !arr.length) return []
    return arr.map((item) => ({
      name: String(item.source ?? "Unknown"),
      clicks: Number(item.clicks ?? item.count ?? 0),
    }))
  }

  const { totalClicks, totalQrScans, daily, geo, device, os, browser, referrers } = data
  const reffer = normalizeReferrers(referrers)


  const calculateTrend = (arr) => {
    if (!arr || arr.length < 2) return { value: 0, trend: "neutral" }
    const last = arr[arr.length - 1].clicks
    const prev = arr[arr.length - 2].clicks
    const diff = last - prev
    const percent = prev ? Math.round((diff / prev) * 100) : 100
    return { value: percent, trend: diff > 0 ? "up" : diff < 0 ? "down" : "neutral" }
  }

  const clicksTrend = calculateTrend(safe(daily, "date"))
  const avgClicksPerDay = daily.length ? Math.round(daily.reduce((a, b) => a + b.clicks, 0) / daily.length) : 0


  const StatCard = ({ icon, label, value, trend, color = "#6366f1" }) => (
    <div style={styles.card}>
      <div style={{ padding: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <p style={styles.statLabel}>{label}</p>
            <p style={styles.statValue}>{typeof value === "number" ? value.toLocaleString() : value}</p>
            {trend && (
              <span style={{
                ...styles.badge,
                background: trend.trend === "up" ? "#dcfce7" : trend.trend === "down" ? "#fee2e2" : "#f1f5f9",
                color: trend.trend === "up" ? "#16a34a" : trend.trend === "down" ? "#dc2626" : "#64748b",
                marginTop: "0.5rem"
              }}>
                {trend.trend === "up" ? "↑" : trend.trend === "down" ? "↓" : "−"} {Math.abs(trend.value)}% vs yesterday
              </span>
            )}
          </div>
          <div style={{
            width: "48px",
            height: "48px",
            borderRadius: "12px",
            background: `${color}10`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.25rem",
          }}>{icon}</div>
        </div>
      </div>
    </div>
  )

  const BreakdownList = ({ data, title, icon, color = "#6366f1" }) => {
    const sorted = safe(data).sort((a,b)=>b.clicks-a.clicks).slice(0, TOP_N)
    const maxClicks = Math.max(...sorted.map(d=>d.clicks),1)
    return (
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h5 style={styles.cardTitle}><span>{icon}</span> {title}</h5>
        </div>
        <div style={{ padding: "1rem 1.5rem" }}>
          {sorted.map((item, idx)=>(
            <div key={idx} style={{ marginBottom: idx < sorted.length-1 ? "1rem" : 0 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"0.375rem" }}>
                <span style={{ fontSize:"0.875rem", fontWeight:500, color:"#334155" }}>{item.name}</span>
                <span style={{ fontSize:"0.875rem", fontWeight:600, color:"#0f172a" }}>{item.clicks.toLocaleString()}</span>
              </div>
              <div style={{ height:"6px", background:"#f1f5f9", borderRadius:"3px", overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${(item.clicks/maxClicks)*100}%`, background: color, borderRadius:"3px", transition:"width 0.3s ease"}}/>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <>
      <Navbar />
      <div style={styles.page} className="container">
        {/* Header */}
        <div style={{ marginBottom: "2rem", display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:"1rem" }}>
          <div>
            <h1 style={{ margin:"0 0 0.375rem", fontSize:"1.5rem", fontWeight:700, color:"#0f172a" }}>Link Analytics</h1>
            <span style={{ fontSize:"0.8125rem", color:"#94a3b8" }}>You're veiwing analytics for this url <i>{data.linkInfo.shortUrl}</i></span>
          
          </div>
          {lastUpdated && <div style={{ display:"flex", alignItems:"center", gap:"1rem" }}>
            <span style={{ fontSize:"0.8125rem", color:"#94a3b8" }}>Source URL: {data.linkInfo.originalUrl}</span>
            
          </div>}
        </div>

        {/* Stats */}
        <div className="row g-4 mb-4">
          <div className="col-md-4"><StatCard icon="📊" label="Total Clicks" value={totalClicks} trend={clicksTrend} color="#6366f1"/></div>
          <div className="col-md-4"><StatCard icon="📱" label="QR Scans" value={totalQrScans} color="#22c55e"/></div>
          <div className="col-md-4"><StatCard icon="📈" label="Daily Average" value={avgClicksPerDay} color="#f59e0b"/></div>
        </div>

        {/* Charts */}
        <div style={{ ...styles.card, marginBottom:"1.5rem" }}>
          <div style={styles.cardHeader}><h5 style={styles.cardTitle}>📈 Clicks Over Time</h5></div>
          <div style={{ ...styles.cardBody, height:320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={safe(daily,"date")}>
                <defs>
                  <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
                <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={{ stroke:"#e2e8f0" }}/>
                <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false}/>
                <Tooltip content={<CustomTooltip/>}/>
                <Area type="monotone" dataKey="clicks" stroke="#6366f1" strokeWidth={2} fill="url(#colorClicks)"/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <CountryMap data={geo} />
        <br />

        <div className="row g-4 mb-4">
          <div className="col-lg-6"><BreakdownList data={(geo)} title={`Top ${TOP_N} Countries`} icon="🌍" color="#6366f1"/></div>
          <div className="col-lg-6"><BreakdownList data={device} title={`Top ${TOP_N} Devices`} icon="💻" color="#22c55e"/></div>
          <div className="col-lg-6"><BreakdownList data={os} title={`Top ${TOP_N} Platform`} icon="💻" color="#0d6efd"/></div>
          <div className="col-lg-6"><BreakdownList data={browser} title={`Top ${TOP_N} Browsers`} icon="🌐" color="#f59e0b"/></div>
          <div className="col-lg-6"><BreakdownList data={reffer} title={`Top ${TOP_N} Referrers`} icon="🔗" color="#8b5cf6"/></div>
        </div>
      </div>
      <MiniFooter />
    </>
  )
}
